import React, { useEffect } from 'react';
import cn from 'classnames';
import { Divider } from 'antd';
import ScopeAffix from '@base/lib/ScopeAffix';
import ProductImagesLightbox from '@app/product/ProductImagesLightbox';
import ProdInfo from '@app/product/ProdInfo';
import { scrollToAnchor } from 'base/utils/scrollTo';
import AddToCart from '@app/product/AddToCart';
import ProductTips from '@app/product/ProductTips';
import InstructionalManual from '@app/product/InstructionalManual';
import ProductSellingPoints from '@app/product/ProductSellingPoints';
import { IReview, ISpuInfo } from 'base/interfaces/product';
import { DisplayPricesType } from '@base/product/utils';
import { useStoreState } from 'base/store';
import { IAddCartInstance } from '@base/product/useAddCart';
import Link from '@base/lib/ThemeLink';
import { loadMonkeyProducts } from 'base/utils/tracker';
import FeaturesBanner from '@base/features/FeturesBanner';
import { IOldFeatureBanner } from 'mod/feature/interface';
import styles from '@app/product/index.module.less';
import stylesBanner from '@base/features/SwiperBanner/index.module.less';
import { useTrans } from 'base/utils/i18n';

type IProps = {
    spuInfo: ISpuInfo;
    bigPicRef: any;
    reviewInfo: IReview;
    addCart: IAddCartInstance;
    isQuickView?: boolean; // 展示详情页链接
    onCloseQuickView?: () => void; //关闭quick view
    isShowAddCart?: boolean; // 展示添加购物车
    canJump?: boolean; // 是否可以跳转到商品详情页
    hideProductTips?: boolean; // 是否隐藏 ProductTips
}

const THUMB_IMAGE_SIZE = 555; //商品展示图的尺寸

const ProductDetailInfo: React.FC<IProps> = (props) => {
    const {
        spuInfo,
        bigPicRef,
        reviewInfo,
        addCart,
        isQuickView = false,
        onCloseQuickView,
        isShowAddCart = true,
        canJump = true,
        hideProductTips = false
    } = props

    const { shownVariant, feature } = addCart

    const _reviewInfo = shownVariant?.review || reviewInfo

    // ManualList有数据就不展示 pdfs
    const pdfs = !!shownVariant.productManualList?.length
        ? shownVariant.productManualList
        : (shownVariant.productPdfVoList || [])

    const prices: DisplayPricesType = {
        shopPrice: shownVariant.shopPrice,
        marketPrice: shownVariant.marketPrice,
        afterDiscountPrice: shownVariant.afterPerDiscountPriceSymbol ? +shownVariant.afterPerDiscountPriceScale : 0
    };

    const { country } = useStoreState('rootStore')
    const { t } = useTrans()

    const _showPrice = +(prices.afterDiscountPrice || prices.shopPrice)

    // 同一个页面同时存在两个（详情页kit商品 Bundle List）
    const warpElId = `affix-scope-${isQuickView ? 'modal' : 'page'}`

    useEffect(() => {
        loadMonkeyProducts();
    }, [shownVariant.variantId]);

    return (
        <div
            id={warpElId}
            className={cn(
                'flex',
                styles['info-wrapper']
            )}
        >
            <div className={styles['top-left']}>
                <ScopeAffix disabled={isQuickView} target={() => document.querySelector(`#${warpElId}`)}>
                    {/* 商品图片 viewer */}
                    <ProductImagesLightbox
                        bigPicRef={bigPicRef}
                        shownVariant={shownVariant}
                        thumbImageSize={THUMB_IMAGE_SIZE}
                    />

                    {isQuickView && canJump && (
                        <div className={styles['see-full-link']}>
                            <Link
                                href={shownVariant.url || `${spuInfo?.url}?variantId=${shownVariant?.productVariantId}`}>
                                <a target="_blank" rel="noopener">
                                    <span>{t('web_product_view_full_info')}</span>
                                </a>
                            </Link>
                        </div>
                    )}
                </ScopeAffix>
            </div>
            <div className={cn(styles['top-right'], 'flex-1')}>
                <div
                    data-t-vid={shownVariant.variantId}
                    data-t-price={Number(shownVariant.dealPrice) || shownVariant.shopPrice}
                    className={styles['monkey-prod-wrap']}
                />
                {/* 商品名称价格信息 */}
                <ProdInfo
                    addCart={addCart}
                    isQuickView={isQuickView}
                    canJump={canJump}
                    onCloseQuickView={onCloseQuickView}
                    shownVariant={shownVariant}
                    spuInfo={spuInfo}
                    reviewInfo={_reviewInfo}
                    onClickReview={() => {
                        scrollToAnchor({ selector: '#review-list' });
                    }}
                />

                {/* tips */}
                {!hideProductTips && <ProductTips shownVariant={shownVariant}/>}

                {/* 加车 */}
                {isShowAddCart && (
                    <AddToCart
                        addCart={addCart}
                        isQuickView={isQuickView}
                        shownVariant={shownVariant}
                    />
                )}

                {/* feature banner */}
                {!!feature?.['product:feature:topRight'] && (
                    <FeaturesBanner
                        dataList={feature['product:feature:topRight'] as Array<IOldFeatureBanner>}
                        banner={{
                            imgShowMode: 'image',
                            navigation: false,
                            className: stylesBanner['swiper-banner-small']
                        }}
                    />
                )}

                {!!pdfs.length && (
                    <>
                        <InstructionalManual
                            list={pdfs}
                            type={shownVariant.productManualList?.length ? 'onlineManual' : 'pdf'}
                        />

                        <Divider style={{ margin: '20px 0 20px' }}/>
                    </>
                )}

                {/* 商品卖点 */}
                <ProductSellingPoints shownVariant={shownVariant}/>
            </div>
        </div>
    );
}

export default React.memo(ProductDetailInfo)
